/**
 * @file: useOrderDrafts.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { OrderDraft } from '@euler/app/flows/order/types';
import { isNotNull } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useOrderDrafts = () => {
  const { draftId, loadDraft, showDraftModal$, mode, type } =
    useOrderFlowContext();
  const { draftManager } = useOrderContext();
  const [drafts] = useBehaviorSubject(draftManager.drafts$);
  const availableDraftIdList = useMemo(
    () =>
      (mode === 'update' || type === 'child' ? [] : drafts)
        ?.filter(x => x.id !== draftId)
        .map(x => x.id)
        .join(',') ?? '',
    [draftId, drafts, mode, type],
  );

  const currentDraft =
    mode === 'update' || type === 'child'
      ? undefined
      : drafts?.find(x => x.id === draftId);
  const [availableDrafts, setAvailableDrafts] = useState<OrderDraft[]>([]);

  const updateAvailableDrafts = usePersistFn((draftIds: string[]) => {
    setAvailableDrafts(
      draftIds.map(id => drafts?.find(x => x.id === id)).filter(isNotNull),
    );
  });

  useEffect(() => {
    updateAvailableDrafts(availableDraftIdList.split(','));
  }, [availableDraftIdList, updateAvailableDrafts]);

  const showOrderDraftsModal = useCallback(() => {
    showDraftModal$.next(true);
  }, [showDraftModal$]);

  const clearDrafts = usePersistFn(() => {
    draftManager.clearDraftExcept(draftId);
  });

  return {
    currentDraft,
    availableDrafts,
    loadDraft,
    clearDrafts,
    showOrderDraftsModal,
    draftManager,
  };
};
